<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbs" />

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
    </div>

    <div v-else>
      <p class="di-subtitle">Conecte os dados do seu evento com serviços externos</p>

      <div class="integrations-grid">
        <!-- Provider cards -->
        <div
          v-for="provider in providerCards"
          :key="provider.key"
          class="int-card"
          :class="{
            active: provider.integration && provider.integration.is_active,
            inactive: provider.integration && !provider.integration.is_active,
            unconfigured: !provider.integration
          }"
          @click="onCardClick(provider)"
        >
          <div class="int-card-top">
            <div class="int-card-icon" :class="provider.iconClass">
              <img
                v-if="provider.logo"
                :src="provider.logo"
                style="width: 26px; height: 26px;"
              />
              <i v-else :class="provider.icon" style="font-size: 26px;"></i>
            </div>
            <span
              v-if="provider.integration && provider.integration.is_active"
              class="int-badge active-badge"
            >&#9679; Ativo</span>
            <span
              v-else-if="provider.integration && !provider.integration.is_active"
              class="int-badge inactive-badge"
            >Desativado</span>
            <!-- Sem badge quando nunca foi configurado -->
          </div>
          <div class="int-card-name">{{ provider.name }}</div>
          <div class="int-card-desc">{{ provider.description }}</div>

          <!-- Stats (has integrations) -->
          <div v-if="provider.integration && provider.integration.is_active" class="int-stats">
            <div class="int-stat">
              <div class="int-stat-value">{{ provider.integrationCount }}</div>
              <div class="int-stat-label">{{ provider.integrationCount === 1 ? 'Integração' : 'Integrações' }}</div>
            </div>
            <div class="int-stat">
              <div class="int-stat-value" :style="{ color: syncStatusColor(provider.integration.last_sync_status) }">
                {{ syncStatusIcon(provider.integration.last_sync_status) }}
              </div>
              <div class="int-stat-label">Último sync</div>
            </div>
            <div class="int-stat">
              <div class="int-stat-value">{{ timeAgo(provider.integration.last_synced_at) }}</div>
              <div class="int-stat-label">Atrás</div>
            </div>
          </div>

          <!-- Has integrations but all inactive -->
          <div v-else-if="provider.integration && !provider.integration.is_active" class="int-card-action">
            {{ provider.integrationCount }} {{ provider.integrationCount === 1 ? 'integração' : 'integrações' }} · Gerenciar <i class="bx bx-right-arrow-alt"></i>
          </div>

          <!-- Not configured yet -->
          <div v-else class="int-card-action">
            Clique para configurar <i class="bx bx-right-arrow-alt"></i>
          </div>
        </div>

        <!-- Coming Soon: HubSpot -->
        <div class="int-card disabled">
          <div class="int-card-top">
            <div class="int-card-icon hubspot">
              <i class="bx bxl-hubspot" style="font-size: 26px;"></i>
            </div>
            <span class="int-badge soon-badge">Em breve</span>
          </div>
          <div class="int-card-name">HubSpot</div>
          <div class="int-card-desc">Sincronize contatos e listas com o CRM do HubSpot.</div>
        </div>

        <!-- Coming Soon: Webhook -->
        <div class="int-card disabled">
          <div class="int-card-top">
            <div class="int-card-icon webhook">
              <i class="bx bx-code-alt" style="font-size: 26px;"></i>
            </div>
            <span class="int-badge soon-badge">Em breve</span>
          </div>
          <div class="int-card-name">Webhook</div>
          <div class="int-card-desc">Dispare eventos HTTP para qualquer endpoint.</div>
        </div>

        <!-- Coming Soon: Zapier -->
        <div class="int-card disabled">
          <div class="int-card-top">
            <div class="int-card-icon zapier">
              <i class="bx bx-bolt-circle" style="font-size: 26px;"></i>
            </div>
            <span class="int-badge soon-badge">Em breve</span>
          </div>
          <div class="int-card-name">Zapier</div>
          <div class="int-card-desc">Conecte com 5.000+ apps via automações.</div>
        </div>
      </div>
    </div>

    <!-- Wizard Modal (new integration) -->
    <WizardModal
      :show="showWizard"
      :form="wizardForm"
      :providers="providers"
      :availableFields="availableFields"
      :isEditing="false"
      :saving="saving"
      @save="saveNewIntegration"
      @close="showWizard = false"
    />
  </Layout>
</template>

<script>
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import appConfig from '@/app.config'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import { mapActions, mapState } from 'vuex'
import WizardModal from './components/WizardModal.vue'

export default {
  components: { Layout, PageHeader, WizardModal },
  name: 'data-integrations-index',
  page: {
    title: 'Integrações',
    meta: [{ name: 'description', content: appConfig.description }]
  },
  data () {
    return {
      loading: true,
      showWizard: false,
      saving: false,
      wizardForm: this.getEmptyForm(),
      title: 'Integrações',
      breadcrumbs: [
        { text: 'Configurações', href: '/' },
        { text: 'Integrações', active: true }
      ],
      providerDefaults: [
        {
          key: 'google_sheets',
          name: 'Google Sheets',
          description: 'Sincronize participantes com uma planilha Google Sheets.',
          icon: 'bx bxl-google',
          iconClass: 'sheets',
          logo: 'https://upload.wikimedia.org/wikipedia/commons/a/ae/Google_Sheets_2020_Logo.svg'
        },
        {
          key: 'sympla',
          name: 'Sympla',
          description: 'Verifique pagamentos na Sympla e confirme participantes automaticamente.',
          icon: 'mdi mdi-alpha-s-box',
          iconClass: 'sympla',
          logo: null
        },
        {
          key: 'whatsapp_positus',
          name: 'WhatsApp',
          description: 'Use suas proprias credenciais para envio de mensagens via WhatsApp.',
          icon: 'bx bxl-whatsapp',
          iconClass: 'whatsapp',
          logo: null
        },
        {
          key: 'eventfy_app',
          name: 'App Eventfy',
          description: 'Envie participantes para o App Eventfy quando atingem o status configurado.',
          icon: 'bx bx-mobile-alt',
          iconClass: 'eventfy',
          logo: null
        }
      ]
    }
  },
  computed: {
    ...mapState('dataIntegrations', ['integrations', 'providers', 'availableFields']),
    providerCards () {
      return this.providerDefaults.map(p => {
        const allOfProvider = this.integrations.filter(i => i.provider === p.key)
        // Pick the most recently synced integration for summary display
        let latest = null
        if (allOfProvider.length) {
          latest = allOfProvider.reduce((best, cur) => {
            if (!best) return cur
            if (!cur.last_synced_at) return best
            if (!best.last_synced_at) return cur
            return new Date(cur.last_synced_at) > new Date(best.last_synced_at) ? cur : best
          }, null)
        }
        return { ...p, integration: latest, integrationCount: allOfProvider.length }
      })
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    ...mapActions('dataIntegrations', {
      fetchIntegrations: 'GetIntegrations',
      fetchProviders: 'GetProviders',
      fetchAvailableFields: 'GetAvailableFields',
      saveIntegration: 'SaveIntegration'
    }),
    getEmptyForm () {
      return {
        name: '',
        provider: 'google_sheets',
        config: {},
        sync_frequency: 'manual',
        filters: { statuses: [], group_ids: [], tag_ids: [] },
        sync_direction: 'push',
        is_active: true,
        field_mappings: []
      }
    },
    async loadData () {
      this.loading = true
      try {
        await this.fetchIntegrations()
      } catch (err) {
        // silent
      }
      this.loading = false
    },
    onCardClick (provider) {
      this.$router.push({ name: 'data-integration-provider', params: { provider: provider.key } })
    },
    async openNewWizard (providerKey) {
      this.wizardForm = this.getEmptyForm()
      this.wizardForm.provider = providerKey

      try {
        if (!this.providers.length) await this.fetchProviders()
        if (!this.availableFields.length) await this.fetchAvailableFields()
      } catch (err) {
        // continue
      }

      const provider = this.providers.find(p => p.key === providerKey)
      if (provider) {
        // Set sync_direction and default_frequency from provider metadata
        if (provider.sync_direction) {
          this.wizardForm.sync_direction = provider.sync_direction
        }
        if (provider.default_frequency) {
          this.wizardForm.sync_frequency = provider.default_frequency
        }
        const cfg = {}
        ;(provider.config_fields || []).forEach(f => {
          cfg[f.key] = f.default !== undefined ? f.default : ''
        })
        this.wizardForm.config = cfg
      }

      this.showWizard = true
    },
    async saveNewIntegration () {
      this.saving = true
      try {
        const res = await this.saveIntegration(this.wizardForm)
        this.showWizard = false
        successMessage('Integração criada com sucesso.', 2500)
        const newId = res.data.data ? res.data.data.id : res.data.id
        if (newId) {
          this.$router.push({ name: 'data-integration-form', params: { id: newId } })
        } else {
          await this.loadData()
        }
      } catch (err) {
        errorMessage('Falha ao criar: ' + (err.message || 'Erro desconhecido'))
      }
      this.saving = false
    },
    syncStatusColor (status) {
      return {
        success: '#34c38f',
        error: '#f46a6a',
        partial: '#f1b44c',
        started: '#2563eb'
      }[status] || '#8d92a9'
    },
    syncStatusIcon (status) {
      return {
        success: '✓',
        error: '✗',
        partial: '!',
        started: '...'
      }[status] || '—'
    },
    timeAgo (dateStr) {
      if (!dateStr) return '—'
      const diff = Date.now() - new Date(dateStr).getTime()
      const minutes = Math.floor(diff / 60000)
      if (minutes < 1) return 'Agora'
      if (minutes < 60) return `${minutes} min`
      const hours = Math.floor(minutes / 60)
      if (hours < 24) return `${hours}h`
      const days = Math.floor(hours / 24)
      return `${days}d`
    }
  }
}
</script>

<style scoped>
.di-subtitle {
  font-size: 13px;
  color: #8d92a9;
  margin-bottom: 24px;
}

/* Grid */
.integrations-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
}

/* Card Base */
.int-card {
  background: #fff;
  border: 1px solid #e2e5ed;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03);
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}
.int-card:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.07), 0 1px 4px rgba(0, 0, 0, 0.04);
  transform: translateY(-2px);
  border-color: #d0d4e0;
}

/* Active */
.int-card.active { border-color: #a7f3d0; }
.int-card.active::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  background: #34c38f;
}

/* Inactive (was configured but disabled) */
.int-card.inactive { border-color: #e2e5ed; }
.int-card.inactive .int-card-icon { opacity: 0.4; }

/* Unconfigured (never set up) */
.int-card.unconfigured { border-color: #e2e5ed; }

/* Disabled (coming soon) */
.int-card.disabled {
  opacity: 0.55;
  cursor: default;
  pointer-events: none;
}

/* Card top */
.int-card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
}

/* Icon */
.int-card-icon {
  width: 48px; height: 48px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.int-card-icon.sheets { background: #e8f5e9; color: #0f9d58; }
.int-card-icon.sympla { background: #eef0ff; color: #4253f0; }
.int-card-icon.whatsapp { background: #e8f8ef; color: #25d366; }
.int-card-icon.eventfy { background: #eef0ff; color: #6d4aff; }
.int-card-icon.hubspot { background: #fff0e6; color: #ff7a59; }
.int-card-icon.webhook { background: #f0ebff; color: #7c3aed; }
.int-card-icon.zapier { background: #fff5e6; color: #ff4a00; }

/* Badge */
.int-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 5px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.int-badge.active-badge { background: #d4f5e7; color: #0d7a5f; }
.int-badge.inactive-badge {
  background: #f8f9fb;
  color: #8d92a9;
  border: 1px solid #edf0f5;
}
.int-badge.soon-badge { background: #fef3d4; color: #92600e; }

/* Card text */
.int-card-name { font-size: 15px; font-weight: 600; margin-bottom: 4px; }
.int-card-desc {
  font-size: 12px;
  color: #8d92a9;
  line-height: 1.5;
  margin-bottom: 16px;
}

/* Stats bar */
.int-stats {
  display: flex;
  border-top: 1px solid #edf0f5;
  margin: 0 -24px -24px;
}
.int-stat {
  flex: 1;
  padding: 12px 16px;
  text-align: center;
  border-right: 1px solid #edf0f5;
}
.int-stat:last-child { border-right: none; }
.int-stat-value {
  font-size: 15px;
  font-weight: 700;
  line-height: 1;
}
.int-stat-label {
  font-size: 10px;
  color: #8d92a9;
  margin-top: 3px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* Action link */
.int-card-action {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #2563eb;
  margin-top: 4px;
}
.int-card-action i { transition: transform 0.2s; font-size: 16px; }
.int-card:hover .int-card-action i { transform: translateX(3px); }

@media (max-width: 900px) {
  .integrations-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 600px) {
  .integrations-grid { grid-template-columns: 1fr; }
}
</style>
